import { MutationTree } from 'vuex';
import { EnumPlans } from '@/enums/Plans';

export type ContactEditSettings = {
  contactId: string;
  filterId?: number;
  showTeam?: boolean;
  showAll?: boolean;
};

export type TemplateRoleSettings = {
  recipientId: string;
  documentId?: string;
};

export type RecipientEditSettings = {
  recipientId: string;
  documentId?: string;
  documentData?: {};
  assignrole?: string;
  toggleAuth?: boolean;
  query?: string;
};

export enum SettingsEnum {
  None = 'NONE',
  Tag = 'TAG',
  Webhook = 'WEBHOOK',
  Team = 'TEAM',
  Theme = 'THEME',
  User = 'USER',
  BrandingGuide = 'BRANDINGGUIDE',
  DataProtection = 'DATAPROTECTION',
  EmailTemplate = 'EMAILTEMPLATE',
}

export enum DocumentEnum {
  None = 'NONE',
  Delete = 'DELETE',
  Error = 'ERROR',
  Fields = 'FIELDS',
  Notification = 'NOTIFICATION',
  Pages = 'PAGES',
  Samples = 'SAMPLES',
  Template = 'TEMPLATE',
  Upload = 'UPLOAD',
  Import = 'IMPORT',
  Attachment = 'ATTACHMENT',
  Video = 'VIDEO',
  Send = 'SEND',
  Summary = 'SUMMARY',
}

export enum SettingsIntegrationEnum {
  None = 'NONE',
  Apsislead = 'APSISLEAD',
  Api = 'API',
  Adroll = 'ADROLL',
  AuthorizeNet = 'AUTHORIZENET',
  Bluesnap = 'BLUESNAP',
  ChangeEmail = 'CHANGEEMAIL',
  Closeio = 'CLOSEIO',
  Conversion = 'CONVERSION',
  Docs = 'DOCS',
  Email = 'EMAIL',
  Freshsales = 'FRESHSALES',
  Facebook = 'FACEBOOK',
  Hubspot = 'HUBSPOT',
  Gatv = 'GATV',
  Ga = 'GA',
  Hubspotsales = 'HUBSPOTSALES',
  Linkedinsales = 'LINKEDINSALES',
  Limecrm = 'LIMECRM',
  Marketo = 'MARKETO',
  Pardot = 'PARDOT',
  Perfectaudience = 'PERFECTAUDIENCE',
  Phone = 'PHONE',
  Onelogin = 'ONELOGIN',
  Recurly = 'RECURLY',
  Slack = 'SLACK',
  Upsales = 'UPSALES',
  Twitter = 'TWITTER',
  Zapier = 'ZAPIER',
  Adword = 'ADWORD',
  Drive = 'DRIVE',
  G2crowd = 'G2CROWD',
  Stripe = 'STRIPE',
  Saml = 'SAML',
  Pipedrive = 'PIPEDRIVE',
  Reco = 'RECO',
}

export enum DealActionEnum {
  None = 'NONE',
  ReminderSms = 'remindersms',
  RecipientSms = 'recipientsms',
  DealValue = 'dealvalue',
  DocumentName = 'documentname',
  Delete = 'delete',
  Recall = 'recall',
  DeleteFolder = 'deletefolder',
  Lost = 'lost',
  Activities = 'activities',
  UploadSigned = 'uploadsigned',
  DetailedLog = 'detailedlog',
  ActivityLog = 'activitylog',
  Sentemail = 'sentemail',
  Marketing = 'marketing',
  Integration = 'integration',
  ReminderEmail = 'reminderemail',
  EmailClient = 'emailclient',
  RecallSigner = 'recallsigner',
  Reschedule = 'reschedule',
  AddRecipient = 'addrecipient',
  UpdateNotification = 'updatenotification',
  Expiration = 'expiration',
  ReminderVideo = 'remindervideo',
}

export type SettingsTagData = {
  tagId;
};

export type SettingsEmailTemplate = {
  emailTemplateId;
};

export type SettingsWebhookData = {
  webhookId;
};

export type SettingsUserData = {
  userId;
};

export type SettingDataProtectionData = {
  removeSigned;
  removeInactive;
};

export type SettingsData =
  | {}
  | SettingsTagData
  | SettingsWebhookData
  | SettingsEmailTemplate
  | SettingsUserData
  | SettingDataProtectionData;

export type DocumentData = {
  documentId: string;
  documentData?: {};
  documentFunctions?: {};
};

export type FieldActionData = {
  currentField?: any;
  documentId?: string;
  documentData?: {};
};

export type DealActionData = {
  dealAction: DealActionEnum;
  documentId?: string;
  documentData?: {};
  recipientId?: string;
  folderId?: string;
};

export type AttachmentActionData = {
  attachmentAction?: any;
  attachmentId?: string;
};

export type DocumentNotificationData = {
  documentId: string;
  notificationId?: string;
  userId?: string;
};

export type DocumentOwnerData = {
  documentId: string;
  userId?: string;
};

export type ArchiveUploadData = {
  folderId: string;
};

export type ModalState = {
  modalIsLoading: boolean;
  showArchiveUpload: boolean;
  archiveUploadData: ArchiveUploadData;
  showContactEdit: boolean;
  contactEditSettings: ContactEditSettings;
  showTemplateRole: boolean;
  templateRoleSettings: TemplateRoleSettings;
  showRecipientEdit: boolean;
  recipientEditSettings: RecipientEditSettings;
  showSettings: SettingsEnum;
  settingsData: SettingsData;
  showSettingsIntegration: SettingsIntegrationEnum;
  showDocument: DocumentEnum;
  documentData: DocumentData;
  showFieldAction: boolean;
  fieldActionData: FieldActionData;
  showDealAction: boolean;
  dealActionData: DealActionData;
  attachmentActionData: AttachmentActionData;
  showDocumentNotification: boolean;
  documentNotificationData: DocumentNotificationData;
  showLoginForm: boolean;
  showDocumentOwner: boolean;
  documentOwnerData: DocumentOwnerData;
  upgradeModalPlan: EnumPlans;
  showAccessDeniedModal: boolean;
};

export const initalState: ModalState = {
  modalIsLoading: false,
  showArchiveUpload: false,
  archiveUploadData: {
    folderId: null,
  },
  showContactEdit: false,
  contactEditSettings: {
    contactId: '0',
  },
  showTemplateRole: false,
  templateRoleSettings: {
    recipientId: '0',
  },
  showRecipientEdit: false,
  recipientEditSettings: {
    recipientId: '0',
    assignrole: '',
  },
  showSettings: SettingsEnum.None,
  settingsData: {},
  showSettingsIntegration: SettingsIntegrationEnum.None,
  showDocument: DocumentEnum.None,
  showDocumentNotification: false,
  documentNotificationData: {
    documentId: '0',
  },
  documentData: {
    documentId: '0',
  },
  showFieldAction: false,
  fieldActionData: {},
  showDealAction: false,
  dealActionData: {
    dealAction: DealActionEnum.None,
  },
  attachmentActionData: {},
  showLoginForm: false,
  showDocumentOwner: false,
  documentOwnerData: {
    documentId: '0',
  },
  upgradeModalPlan: null,
  showAccessDeniedModal: false,
};

function resetAllModals(state: ModalState) {
  state.showArchiveUpload = false;
  state.showContactEdit = false;
  state.showRecipientEdit = false;
  state.showTemplateRole = false;
  state.showSettings = SettingsEnum.None;
  state.showSettingsIntegration = SettingsIntegrationEnum.None;
  state.showDocument = DocumentEnum.None;
  state.showDocumentNotification = false;
  state.showFieldAction = false;
  state.showDealAction = false;
  state.showLoginForm = false;
  state.showDocumentOwner = false;
  state.upgradeModalPlan = null;
  state.showAccessDeniedModal = false;
}

export const mutations: MutationTree<ModalState> = {
  setModalIsLoading(state, payload: boolean) {
    state.modalIsLoading = payload;
  },
  setToggleArchiveUpload(state, payload: boolean) {
    resetAllModals(state);
    state.showArchiveUpload = payload;
  },
  setArchiveUploadData(state, payload: ArchiveUploadData) {
    state.archiveUploadData = payload;
  },
  setToggleContactEdit(state, payload: boolean) {
    resetAllModals(state);
    state.showContactEdit = payload;
  },
  setContactEditSettings(state, payload: ContactEditSettings) {
    state.contactEditSettings = payload;
  },
  setToggleTemplateRole(state, payload: boolean) {
    resetAllModals(state);
    state.showTemplateRole = payload;
  },
  setTemplateRoleSettings(state, payload: TemplateRoleSettings) {
    state.templateRoleSettings = payload;
  },
  setToggleRecipientEdit(state, payload: boolean) {
    resetAllModals(state);
    state.showRecipientEdit = payload;
  },
  setRecipientEditSettings(state, payload: RecipientEditSettings) {
    state.recipientEditSettings = payload;
  },
  setToggleSettings(state, payload: SettingsEnum) {
    resetAllModals(state);
    state.showSettings = payload;
  },
  setSettingsData(state, payload: SettingsData) {
    state.settingsData = payload;
  },
  setToggleSettingsIntegration(state, payload: SettingsIntegrationEnum) {
    resetAllModals(state);
    state.showSettingsIntegration = payload;
  },
  setToggleDocument(state, payload: DocumentEnum) {
    resetAllModals(state);
    state.showDocument = payload;
  },
  setToggleDocumentNotification(state, payload: boolean) {
    state.showDocumentNotification = payload;
  },
  setDocumentNotificationData(state, payload: DocumentNotificationData) {
    state.documentNotificationData = payload;
  },
  setDocumentData(state, payload: DocumentData) {
    state.documentData = payload;
  },
  setToggleFieldAction(state, payload: boolean) {
    resetAllModals(state);
    state.showFieldAction = payload;
  },
  setFieldActionData(state, payload: DealActionData) {
    state.fieldActionData = payload;
  },
  setToggleDealAction(state, payload: boolean) {
    resetAllModals(state);
    state.showDealAction = payload;
  },
  setDealActionData(state, payload: DealActionData) {
    state.dealActionData = payload;
  },
  setAttachmentActionData(state, payload: AttachmentActionData) {
    state.attachmentActionData = payload;
  },
  setToggleLoginForm(state, payload: boolean) {
    state.showLoginForm = payload;
  },
  setToggleDocumentOwner(state, payload: boolean) {
    state.showDocumentOwner = payload;
  },
  setDocumentOwnerData(state, payload: DocumentOwnerData) {
    state.documentOwnerData = payload;
  },
  setToggleUpgradeModal(state, payload: EnumPlans) {
    state.upgradeModalPlan = payload;
  },
  setToggleAccessDeniedModal(state, payload: boolean) {
    state.showAccessDeniedModal = payload;
  },
};
